import { useEffect, useMemo, useState } from "react";
import { Calendar, CheckCircle2, Send, X, Inbox, Minus, Plus, Package, Sparkles, MessageSquare } from "lucide-react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useBookDemo } from "@/context/BookDemoContext";
import { submitDemoRequest } from "@/lib/api";
import { PUBLIC_PLANS, PUBLIC_ADDONS, formatKr } from "@/lib/pricingCatalog";

const initialForm = { name: "", company: "", email: "", phone: "", industry: "", message: "" };
const initialAddons = PUBLIC_ADDONS.reduce((acc, a) => ({ ...acc, [a.id]: 0 }), {});

const MODE_COPY = {
    demo: {
        eyebrow: "Demo",
        eyebrowIcon: Calendar,
        title: "Book gratis demo",
        description: "Vælg en pakke + evt. ekstra, og fortæl os hvad I gerne vil have løst. Vi kontakter dig hurtigst muligt.",
        submit: "Send demoforespørgsel",
        successTitle: "Tak!",
        successDescription: "Din demoforespørgsel er modtaget. Vi kontakter dig hurtigst muligt.",
    },
    cart: {
        eyebrow: "Forespørgsel",
        eyebrowIcon: Inbox,
        title: "Send forespørgsel",
        description: "Udfyld dine oplysninger og bekræft dit valg, så kontakter vi dig med et tilpasset setup.",
        submit: "Send forespørgsel",
        successTitle: "Tak!",
        successDescription: "Din forespørgsel er modtaget. Vi kontakter dig hurtigst muligt.",
    },
};

export default function BookDemoModal() {
    const { isOpen, close, mode, prefillMessage, prefillSelection } = useBookDemo();
    const [form, setForm] = useState(initialForm);
    const [plan, setPlan] = useState(null); // "starter" | "pro" | null
    const [addons, setAddons] = useState(initialAddons);
    const [submitting, setSubmitting] = useState(false);
    const [sent, setSent] = useState(false);

    const copy = MODE_COPY[mode] || MODE_COPY.demo;
    const EyebrowIcon = copy.eyebrowIcon;

    // Apply prefill once when the modal opens
    useEffect(() => {
        if (!isOpen) return;
        if (prefillMessage) setForm((f) => ({ ...f, message: prefillMessage }));
        if (prefillSelection) {
            if (prefillSelection.plan) setPlan(prefillSelection.plan);
            if (prefillSelection.addons) {
                setAddons((cur) => ({ ...cur, ...prefillSelection.addons }));
            }
        }
    }, [isOpen, prefillMessage, prefillSelection]);

    const handleChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
    const bumpAddon = (id, delta) => setAddons((a) => ({ ...a, [id]: Math.max(0, Math.min(99, (a[id] || 0) + delta)) }));

    const monthlyTotal = useMemo(() => {
        const planMonthly = plan ? (PUBLIC_PLANS.find((p) => p.id === plan)?.monthly || 0) : 0;
        const addonsMonthly = PUBLIC_ADDONS.reduce((sum, a) => sum + (addons[a.id] || 0) * a.unit_price, 0);
        return planMonthly + addonsMonthly;
    }, [plan, addons]);

    const handleOpenChange = (next) => {
        if (!next) {
            close();
            setTimeout(() => {
                setSent(false);
                setForm(initialForm);
                setPlan(null);
                setAddons(initialAddons);
            }, 250);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim()) {
            toast.error("Udfyld venligst navn og email.");
            return;
        }
        setSubmitting(true);
        try {
            const selection = {
                plan: plan || null,
                addons: Object.fromEntries(Object.entries(addons).filter(([, v]) => v > 0)),
                general_question: form.message?.trim() || "",
            };
            await submitDemoRequest({ ...form, selection });
            setSent(true);
            toast.success("Tak — vi vender tilbage hurtigst muligt.");
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Noget gik galt. Prøv igen.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <DialogPrimitive.Root open={isOpen} onOpenChange={handleOpenChange}>
            <DialogPrimitive.Portal>
                <DialogPrimitive.Overlay
                    className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
                    data-testid="book-demo-overlay"
                />
                <DialogPrimitive.Content
                    data-testid="book-demo-modal"
                    data-mode={mode}
                    className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[calc(100vw-2rem)] sm:w-full max-w-2xl bg-white rounded-2xl shadow-2xl shadow-slate-900/20 border border-slate-200/70 p-6 sm:p-8 max-h-[calc(100vh-2rem)] overflow-y-auto data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
                >
                    <DialogPrimitive.Close
                        data-testid="book-demo-close"
                        className="absolute right-4 top-4 w-9 h-9 inline-flex items-center justify-center rounded-full text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors"
                        aria-label="Luk"
                    >
                        <X className="w-4 h-4" />
                    </DialogPrimitive.Close>

                    {sent ? (
                        <div className="text-center py-4" data-testid="book-demo-success">
                            <div className="mx-auto w-14 h-14 rounded-2xl bg-emerald-50 text-emerald-600 inline-flex items-center justify-center">
                                <CheckCircle2 className="w-7 h-7" />
                            </div>
                            <DialogPrimitive.Title className="mt-5 text-2xl font-bold text-slate-900">{copy.successTitle}</DialogPrimitive.Title>
                            <DialogPrimitive.Description className="mt-2 text-slate-600">
                                {copy.successDescription}
                            </DialogPrimitive.Description>
                            <Button
                                data-testid="book-demo-done"
                                onClick={() => handleOpenChange(false)}
                                className="mt-6 rounded-full h-11 px-5 bg-slate-900 hover:bg-slate-800 text-white"
                            >
                                Luk
                            </Button>
                        </div>
                    ) : (
                        <>
                            <div>
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 text-[11px] font-semibold uppercase tracking-wider border border-blue-100">
                                    <EyebrowIcon className="w-3 h-3" />
                                    {copy.eyebrow}
                                </span>
                                <DialogPrimitive.Title className="mt-3 text-2xl font-bold text-slate-900">
                                    {copy.title}
                                </DialogPrimitive.Title>
                                <DialogPrimitive.Description className="mt-2 text-sm text-slate-600 leading-relaxed">
                                    {copy.description}
                                </DialogPrimitive.Description>
                            </div>

                            <form onSubmit={handleSubmit} noValidate className="mt-6 space-y-5" data-testid="book-demo-form">
                                {/* Package selector */}
                                <fieldset className="space-y-2" data-testid="book-demo-plan-group">
                                    <legend className="text-xs font-semibold text-slate-700 uppercase tracking-wider inline-flex items-center gap-1.5"><Package className="w-3 h-3" />Vælg pakke</legend>
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                                        {PUBLIC_PLANS.map((p) => {
                                            const active = plan === p.id;
                                            return (
                                                <button
                                                    type="button"
                                                    key={p.id}
                                                    onClick={() => setPlan(active ? null : p.id)}
                                                    data-testid={`book-demo-plan-${p.id}`}
                                                    data-active={active}
                                                    className={`text-left p-3 rounded-xl border-2 transition ${active ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:border-slate-300 bg-white"}`}
                                                >
                                                    <p className={`text-sm font-bold ${active ? "text-blue-700" : "text-slate-900"}`}>{p.name}</p>
                                                    <p className="text-[11px] text-slate-500 mt-0.5 leading-tight">{p.tagline}</p>
                                                    <p className={`text-sm font-semibold mt-1 ${active ? "text-blue-700" : "text-slate-900"}`}>{formatKr(p.monthly)} / md</p>
                                                </button>
                                            );
                                        })}
                                        <button
                                            type="button"
                                            onClick={() => setPlan(null)}
                                            data-testid="book-demo-plan-undecided"
                                            data-active={plan === null}
                                            className={`text-left p-3 rounded-xl border-2 transition ${plan === null ? "border-slate-400 bg-slate-50" : "border-dashed border-slate-200 hover:border-slate-300 bg-white"}`}
                                        >
                                            <p className="text-sm font-bold text-slate-900">Jeg er i tvivl</p>
                                            <p className="text-[11px] text-slate-500 mt-0.5 leading-tight">Vi finder ud af det sammen</p>
                                            <p className="text-sm font-semibold mt-1 text-slate-400">—</p>
                                        </button>
                                    </div>
                                </fieldset>

                                {/* Addons */}
                                <fieldset className="space-y-2" data-testid="book-demo-addons-group">
                                    <legend className="text-xs font-semibold text-slate-700 uppercase tracking-wider inline-flex items-center gap-1.5"><Sparkles className="w-3 h-3" />Ekstra (valgfrit)</legend>
                                    <div className="space-y-2">
                                        {PUBLIC_ADDONS.map((a) => {
                                            const qty = addons[a.id] || 0;
                                            return (
                                                <div key={a.id} className="flex items-center gap-3 p-2.5 rounded-xl border border-slate-200 bg-white" data-testid={`book-demo-addon-${a.id}`}>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-medium text-slate-900 truncate">{a.name}</p>
                                                        <p className="text-[11px] text-slate-500">{formatKr(a.unit_price)} pr. {a.unit_label}</p>
                                                    </div>
                                                    <div className="flex items-center gap-1.5">
                                                        <button type="button" onClick={() => bumpAddon(a.id, -1)} disabled={qty === 0} data-testid={`book-demo-addon-${a.id}-minus`} className="w-7 h-7 rounded-lg border border-slate-200 inline-flex items-center justify-center text-slate-600 hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed"><Minus className="w-3 h-3" /></button>
                                                        <span className="w-7 text-center text-sm font-semibold tabular-nums" data-testid={`book-demo-addon-${a.id}-qty`}>{qty}</span>
                                                        <button type="button" onClick={() => bumpAddon(a.id, +1)} data-testid={`book-demo-addon-${a.id}-plus`} className="w-7 h-7 rounded-lg border border-slate-200 inline-flex items-center justify-center text-slate-600 hover:bg-slate-50"><Plus className="w-3 h-3" /></button>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    {monthlyTotal > 0 && (
                                        <div className="flex items-center justify-between text-xs text-slate-600 px-1 pt-1" data-testid="book-demo-monthly-total">
                                            <span>Estimeret månedlig pris</span>
                                            <span className="font-bold text-slate-900 text-sm">{formatKr(monthlyTotal)} / md</span>
                                        </div>
                                    )}
                                </fieldset>

                                {/* Contact info */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div className="space-y-1">
                                        <Label htmlFor="bd-name" className="text-xs font-medium text-slate-700">Navn *</Label>
                                        <Input id="bd-name" name="name" value={form.name} onChange={handleChange} placeholder="Dit navn" data-testid="book-demo-name" className="h-10 rounded-lg bg-white border-slate-200" />
                                    </div>
                                    <div className="space-y-1">
                                        <Label htmlFor="bd-company" className="text-xs font-medium text-slate-700">Virksomhed</Label>
                                        <Input id="bd-company" name="company" value={form.company} onChange={handleChange} placeholder="Virksomhed" data-testid="book-demo-company" className="h-10 rounded-lg bg-white border-slate-200" />
                                    </div>
                                    <div className="space-y-1">
                                        <Label htmlFor="bd-email" className="text-xs font-medium text-slate-700">Email *</Label>
                                        <Input id="bd-email" name="email" type="email" value={form.email} onChange={handleChange} placeholder="din@email.dk" data-testid="book-demo-email" className="h-10 rounded-lg bg-white border-slate-200" />
                                    </div>
                                    <div className="space-y-1">
                                        <Label htmlFor="bd-phone" className="text-xs font-medium text-slate-700">Telefon</Label>
                                        <Input id="bd-phone" name="phone" value={form.phone} onChange={handleChange} placeholder="Telefon" data-testid="book-demo-phone" className="h-10 rounded-lg bg-white border-slate-200" />
                                    </div>
                                </div>
                                <div className="space-y-1">
                                    <Label htmlFor="bd-industry" className="text-xs font-medium text-slate-700">Branche</Label>
                                    <Input id="bd-industry" name="industry" value={form.industry} onChange={handleChange} placeholder="Fx Elektriker, VVS, Tømrer…" data-testid="book-demo-industry" className="h-10 rounded-lg bg-white border-slate-200" />
                                </div>

                                {/* General questions */}
                                <div className="space-y-1">
                                    <Label htmlFor="bd-message" className="text-xs font-medium text-slate-700 inline-flex items-center gap-1.5"><MessageSquare className="w-3 h-3" />Generelle spørgsmål</Label>
                                    <Textarea id="bd-message" name="message" value={form.message} onChange={handleChange} placeholder="Spørgsmål, ønsker eller noget vi skal vide om jeres arbejdsgang?" rows={mode === "cart" ? 5 : 3} data-testid="book-demo-message" className="rounded-lg bg-white border-slate-200 text-sm" />
                                </div>

                                <Button
                                    type="submit"
                                    disabled={submitting}
                                    data-testid="book-demo-submit"
                                    className="w-full rounded-full h-12 px-6 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-md disabled:opacity-60"
                                >
                                    <Send className="w-4 h-4 mr-2" />
                                    {submitting ? "Sender…" : copy.submit}
                                </Button>
                            </form>
                        </>
                    )}
                </DialogPrimitive.Content>
            </DialogPrimitive.Portal>
        </DialogPrimitive.Root>
    );
}
